import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const { ok, data } = await api.get('/auth/me');
      if (ok) {
        setUser(data);
      } else {
        setUser(null);
        localStorage.removeItem('session_token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { ok, data } = await api.post('/auth/login', { email, password });
    if (!ok) throw new Error(data.detail || 'Login failed');
    if (data.session_token) localStorage.setItem('session_token', data.session_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, invite_token) => {
    const payload = { email, password, name };
    if (invite_token) payload.invite_token = invite_token;
    const { ok, data } = await api.post('/auth/register', payload);
    if (!ok) throw new Error(data.detail || 'Registration failed');
    if (data.session_token) localStorage.setItem('session_token', data.session_token);
    setUser(data);
    return data;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = process.env.REACT_APP_GOOGLE_AUTH_URL;
    window.location.href = `${authUrl}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const { ok, data } = await api.post('/auth/session', { session_id: sessionId });
    if (!ok) throw new Error(data.detail || 'Google login failed');
    if (data.session_token) localStorage.setItem('session_token', data.session_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout', {}); }
    catch (error) { console.error('Logout error:', error); }
    localStorage.removeItem('session_token');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('impersonating');
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const { ok, data } = await api.post('/auth/forgot-password', { email });
    if (!ok) throw new Error(data.detail || 'Request failed');
    return data;
  };

  const updateSettings = async (settings) => {
    const { ok, data } = await api.patch('/auth/settings', settings);
    if (!ok) throw new Error(data.detail || 'Update failed');
    setUser(data);
    return data;
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    loginWithGoogle,
    processGoogleSession,
    logout,
    forgotPassword,
    updateSettings,
    checkAuth,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
